import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

// ── Helpers ──────────────────────────────────────────────────────────────────

function resolveDayType(dateStr) {
  if (!dateStr) return 'weekday';
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return 'weekday';
  const day = d.getUTCDay();
  return day === 0 || day === 6 ? 'weekend' : 'weekday';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(`${dateStr}T00:00:00Z`).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return dateStr;
  }
}

// ── HND Computing stream normaliser ─────────────────────────────────────────

const HND_COMPUTING_BASE = 'Level 5 Pearson BTEC HND in Computing';

function getCourseDisplayInfo(courseName) {
  if (!courseName) return { key: 'Unknown Course', stream: null };
  if (courseName.startsWith(HND_COMPUTING_BASE)) {
    const m = courseName.match(/\(([^)]+)\)$/);
    return { key: HND_COMPUTING_BASE, stream: m ? m[1] : null };
  }
  return { key: courseName, stream: null };
}

// ── WhatsApp message builder ─────────────────────────────────────────────────

function buildWhatsAppMessage(group, formattedDate) {
  const lines = [];
  lines.push(`📋 *Unscheduled Classes — ${formattedDate}*`);
  lines.push('');
  lines.push(`*Course:* ${group.displayName}${group.courseShortName ? ` (${group.courseShortName})` : ''}`);
  if (group.streams.length > 0) {
    lines.push(`*Streams:* ${group.streams.join(', ')}`);
  }
  lines.push('');
  lines.push('The following batches do not have a scheduled class today:');
  lines.push('');

  for (const sg of group.subGroups) {
    if (sg.groupName) {
      lines.push(`📱 *${sg.groupName}*`);
    }
    for (const b of sg.batches) {
      const label = [b.batchInternalName, b.batchName].filter(Boolean).join(' — ');
      lines.push(`  • ${label}`);
    }
    lines.push('');
  }

  lines.push('Could you please provide the reason why the above classes are unscheduled for today?');
  lines.push('Kindly confirm and update the timetable at your earliest convenience.');
  lines.push('');
  lines.push('Thank you.');
  return lines.join('\n');
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ScheduleTypeBadge({ type }) {
  if (!type) return null;
  const isWeekend = type === 'weekend';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold
        ${isWeekend
          ? 'bg-purple-100 text-purple-700 border border-purple-200'
          : 'bg-blue-100 text-blue-700 border border-blue-200'
        }`}
    >
      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
        {isWeekend
          ? <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
          : <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        }
      </svg>
      {isWeekend ? 'Weekend' : 'Weekday'}
    </span>
  );
}

function StatusBadge({ status }) {
  const colours = {
    ongoing: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'not started': 'bg-gray-100 text-gray-700 border-gray-200',
    'delivery completed': 'bg-blue-100 text-blue-800 border-blue-200',
    'batch completed': 'bg-slate-100 text-slate-700 border-slate-200',
  };
  const cls = colours[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${cls}`}>
      {status || 'Unknown'}
    </span>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function UnscheduledSchedulesNotifications() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const today = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || today);

  const [batches, setBatches] = useState([]);
  const [scheduleType, setScheduleType] = useState(() => resolveDayType(searchParams.get('date') || today));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Search / filter state
  const [searchCourse, setSearchCourse] = useState('');
  const [searchBatch, setSearchBatch] = useState('');

  // Copy-to-clipboard feedback: stores the displayName of the last-copied course
  const [copiedKey, setCopiedKey] = useState(null);

  // ── Load data whenever date changes ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    const load = async () => {
      try {
        const resp = await axiosInstance.get('/admin/timetable/unscheduled-batches', {
          params: { date: selectedDate },
        });
        if (cancelled) return;
        setBatches(resp.data?.unscheduledBatches || []);
        setScheduleType(resp.data?.scheduleType || resolveDayType(selectedDate));
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || 'Failed to load unscheduled batches.');
          setBatches([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [selectedDate]);

  // ── Derived: group by normalised course, sub-group by WhatsApp group name ────
  const groupedByCourse = useMemo(() => {
    const courseTerm = searchCourse.trim().toLowerCase();
    const batchTerm  = searchBatch.trim().toLowerCase();

    const filtered = batches.filter((b) => {
      const { key } = getCourseDisplayInfo(b.courseName);
      const course    = key.toLowerCase();
      const courseShort = (b.courseShortName || '').toLowerCase();
      const batchName = (b.batchInternalName || b.batchName || '').toLowerCase();
      if (courseTerm && !course.includes(courseTerm) && !courseShort.includes(courseTerm)) return false;
      if (batchTerm  && !batchName.includes(batchTerm)) return false;
      return true;
    });

    const map = new Map();
    for (const b of filtered) {
      const { key, stream } = getCourseDisplayInfo(b.courseName);
      if (!map.has(key)) {
        map.set(key, {
          displayName: key,
          courseShortName: b.courseShortName || '',
          courseScheduleType: b.courseScheduleType || '',
          streams: new Set(),
          allBatches: [],
        });
      }
      const entry = map.get(key);
      if (stream) entry.streams.add(stream);
      entry.allBatches.push(b);
    }

    return Array.from(map.values())
      .map((entry) => {
        // Sub-group by whatsappGroupName
        const sgMap = new Map();
        for (const b of entry.allBatches) {
          const sgKey = b.whatsappGroupName || '__none__';
          if (!sgMap.has(sgKey)) sgMap.set(sgKey, { groupName: b.whatsappGroupName || null, batches: [] });
          sgMap.get(sgKey).batches.push(b);
        }
        const subGroups = Array.from(sgMap.values()).sort((a, b) => {
          if (!a.groupName && !b.groupName) return 0;
          if (!a.groupName) return 1;
          if (!b.groupName) return -1;
          return a.groupName.localeCompare(b.groupName);
        });
        return {
          displayName: entry.displayName,
          courseShortName: entry.courseShortName,
          courseScheduleType: entry.courseScheduleType,
          streams: Array.from(entry.streams).sort(),
          subGroups,
          totalCount: entry.allBatches.length,
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [batches, searchCourse, searchBatch]);

  const totalVisible = groupedByCourse.reduce((acc, g) => acc + g.totalCount, 0);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    navigate(`/admin/notifications/unscheduled-schedules?date=${encodeURIComponent(newDate)}`, { replace: true });
  };

  // ── Copy WhatsApp message ─────────────────────────────────────────────────────
  const handleCopyWhatsApp = (group) => {
    const text = buildWhatsAppMessage(group, formatDate(selectedDate));
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(group.displayName);
      setTimeout(() => setCopiedKey(null), 2500);
    }).catch(() => {});
  };

  // ── Export CSV ────────────────────────────────────────────────────────────────
  const handleDownloadExcel = async () => {
    if (!totalVisible) return;
    try {
      const XLSX = await import('xlsx');
      const allRows = groupedByCourse.flatMap((g) =>
        g.subGroups.flatMap((sg) =>
          sg.batches.map((b) => [
            g.displayName,
            b.courseShortName || '',
            b.courseScheduleType || '',
            sg.groupName || '',
            b.batchName || '',
            b.batchInternalName || '',
            b.status || '',
          ]),
        ),
      );
      const headers = ['Course Name', 'Short Name', 'Schedule Type', 'WhatsApp Group', 'Batch Name', 'Internal Name', 'Status'];
      const ws = XLSX.utils.aoa_to_sheet([headers, ...allRows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Unscheduled Batches');
      XLSX.writeFile(wb, `Unscheduled_Batches_${selectedDate}.xlsx`);
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout title="Unscheduled Batches">
      <div className="space-y-6 py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">

        {/* ── Page Header ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              {/* warning icon */}
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100">
                <svg className="h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                </svg>
              </span>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Unscheduled Batches</h1>
                <p className="text-sm text-gray-500">
                  Ongoing {scheduleType} batches with no class scheduled on{' '}
                  <span className="font-medium text-gray-700">{formatDate(selectedDate)}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* date picker */}
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-500">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>

            {/* export button */}
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-500">&nbsp;</label>
              <button
                type="button"
                onClick={handleDownloadExcel}
                disabled={!totalVisible}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>
            </div>
          </div>
        </div>

        {/* ── Summary Strip ────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Total Batches</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{batches.length}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Courses Affected</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {groupedByCourse.length}
            </p>
          </div>
          <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Schedule Type</p>
            <p className="mt-1 capitalize text-2xl font-bold text-amber-800">{scheduleType}</p>
          </div>
        </div>

        {/* ── Search Bar ───────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search course…"
              value={searchCourse}
              onChange={(e) => setSearchCourse(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search batch…"
              value={searchBatch}
              onChange={(e) => setSearchBatch(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>
        </div>

        {/* ── Content ──────────────────────────────────────────────────────────── */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="h-10 w-10 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin" />
          </div>
        ) : batches.length === 0 && !error ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-20">
            <svg className="h-12 w-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-3 text-base font-semibold text-gray-700">All batches are scheduled</p>
            <p className="mt-1 text-sm text-gray-500">
              Every ongoing {scheduleType} batch has a class on {formatDate(selectedDate)}.
            </p>
          </div>
        ) : totalVisible === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white px-6 py-10 text-center text-sm text-gray-500">
            No batches match your search.
          </div>
        ) : (
          <div className="space-y-5">
            {groupedByCourse.map((group) => (
              <div key={group.displayName} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">

                {/* ── Course header ── */}
                <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-5 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-sm font-semibold text-gray-900">{group.displayName}</h2>
                      {group.courseShortName && (
                        <span className="shrink-0 rounded bg-indigo-100 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700">
                          {group.courseShortName}
                        </span>
                      )}
                      {/* Stream badges – only appear when multiple HND variants are merged */}
                      {group.streams.map((stream) => (
                        <span key={stream} className="shrink-0 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700">
                          {stream}
                        </span>
                      ))}
                      <ScheduleTypeBadge type={group.courseScheduleType} />
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                      {group.totalCount} unscheduled
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopyWhatsApp(group)}
                      title="Copy coordinator message for WhatsApp"
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold shadow-sm transition-all
                        ${
                          copiedKey === group.displayName
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700'
                        }`}
                    >
                      {copiedKey === group.displayName ? (
                        <>
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          Copied!
                        </>
                      ) : (
                        <>
                          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                          </svg>
                          Copy for WhatsApp
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* ── Batch table ── */}
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-white">
                      <th className="py-2.5 pl-5 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">#</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Internal Name</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Batch Name</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                      <th className="pl-3 pr-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.subGroups.map((subGroup) => (
                      <React.Fragment key={subGroup.groupName || '__none__'}>

                        {/* WhatsApp group separator row */}
                        {subGroup.groupName ? (
                          <tr className="border-t border-emerald-100 bg-emerald-50/60">
                            <td colSpan={5} className="px-5 py-2">
                              <div className="flex items-center gap-2">
                                <svg className="h-4 w-4 shrink-0 text-emerald-600" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                </svg>
                                <span className="text-xs font-semibold text-emerald-800">{subGroup.groupName}</span>
                                <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                                  {subGroup.batches.length} batch{subGroup.batches.length > 1 ? 'es' : ''}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          group.subGroups.some((sg) => sg.groupName) && (
                            <tr className="border-t border-gray-100 bg-gray-50/40">
                              <td colSpan={5} className="px-5 py-2">
                                <div className="flex items-center gap-2">
                                  <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  </svg>
                                  <span className="text-xs font-medium text-gray-500">No WhatsApp Group</span>
                                  <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500">
                                    {subGroup.batches.length} batch{subGroup.batches.length > 1 ? 'es' : ''}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          )
                        )}

                        {subGroup.batches.map((b, idx) => (
                          <tr key={b.batchId} className="border-t border-gray-50 hover:bg-amber-50/40 transition-colors">
                            <td className="py-3 pl-5 pr-3 text-xs text-gray-400">{idx + 1}</td>
                            <td className="px-3 py-3 font-medium text-gray-900">{b.batchInternalName || '—'}</td>
                            <td className="px-3 py-3 text-gray-600">{b.batchName || '—'}</td>
                            <td className="px-3 py-3"><StatusBadge status={b.status} /></td>
                            <td className="pl-3 pr-5 py-3">
                              <a
                                href={`/admin/schedule?date=${selectedDate}`}
                                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                              >
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Open Timetable
                              </a>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {/* ── Bottom count ─────────────────────────────────────────────────────── */}
        {!loading && totalVisible > 0 && (
          <p className="text-right text-xs text-gray-400">
            Showing {totalVisible} of {batches.length} unscheduled batch{batches.length !== 1 ? 'es' : ''}
          </p>
        )}
      </div>
    </DashboardLayout>
  );
}
